"use client";
import { motion, useScroll, useSpring } from "framer-motion";
import usePublicContacts from '@/hooks/usePublicContacts';
import { Scale, ShieldCheck, FileText, ChevronRight, AlertCircle, HelpCircle, Clock, Gavel } from "lucide-react";
import { useRef } from "react";
import Header from "@/components/landing/Header";
import { useLanguage } from "@/contexts/LanguageContext";
import RichText from "@/components/landing/RichText";
import Footer from "@/components/landing/Footer";
import CTASection from "@/components/landing/CTASection";

/* Icons and anchor ids only — every readable field (title, tag, body,
   the four detail chips) comes from `legal.terms.sections`, index-aligned
   with this list. */
const SECTION_META = [
    { id: "agreement", icon: Gavel },
    { id: "services", icon: Clock },
    { id: "usage", icon: ShieldCheck },
    { id: "liability", icon: AlertCircle },
];

export default function TermsAndConditions() {
  const contacts = usePublicContacts();
    const { t, tv } = useLanguage();
    const sections = tv("legal.terms.sections").map((copy, i) => ({ ...copy, ...SECTION_META[i] }));

    const scrollRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: scrollRef });
    const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

    return (
        <div className="landing-root min-h-screen bg-[#FAFCFF]">
            <Header />
            <main className="min-h-screen bg-[#FAFCFF] selection:bg-[#3D5EE1]/10 selection:text-[#2040CF]">
            {/* Scroll Progress Bar */}
            <motion.div className="fixed top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#2040CF] to-[#3D5EE1] z-[60] origin-left" style={{ scaleX }} />

            {/* Hero */}
            <section className="relative pt-24 pb-12 sm:pt-32 sm:pb-24 overflow-hidden bg-white border-b border-[#E8F4FD]">
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-[#3D5EE1]/10 rounded-full blur-[120px] animate-pulse" />
                    <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-[#3D5EE1]/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: "2s" }} />
                    <div className="absolute inset-0 grid-bg opacity-[0.15]" />
                    <motion.div animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-[20%] left-[10%] opacity-20 hidden lg:block">
                        <Gavel size={60} className="text-[#2040CF]" />
                    </motion.div>
                    <motion.div animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                        className="absolute bottom-[20%] right-[12%] opacity-20 hidden lg:block">
                        <Scale size={80} className="text-[#2040CF]" />
                    </motion.div>
                    <motion.div animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.2, 0.1] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-[40%] right-[25%] opacity-10 hidden lg:block">
                        <ShieldCheck size={40} className="text-[#2040CF]" />
                    </motion.div>
                </div>

                <div className="site-container relative z-10 px-3 sm:px-6">
                    <div className="flex flex-col items-center text-center">
                        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="font-sora font-black text-2xl sm:text-5xl md:text-6xl lg:text-[72px] text-[#0d1117] leading-[1.15] sm:leading-[1.1] tracking-tight mb-4 sm:mb-8">
                            <RichText text={t("legal.terms.heading")} accentClassName="text-gradient-blue" />
                        </motion.h1>

                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
                            className="w-24 h-1.5 bg-gradient-to-r from-[#2040CF] to-[#3D5EE1] rounded-full mb-8" />

                        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
                            className="text-xs sm:text-[17px] md:text-[19px] font-medium text-[#596875] max-w-[700px] leading-relaxed">
                            {t("legal.terms.subtitle")}
                        </motion.p>

                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
                            className="mt-6 sm:mt-10 flex items-center gap-2 sm:gap-3 text-[11px] sm:text-[13px] font-bold text-[#94a3b8]">
                            <Clock size={13} className="sm:w-3.5 sm:h-3.5" />
                            <span>{t("legal.lastUpdated")}</span>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="w-full py-10 sm:py-20 bg-[#FAFCFF]" ref={scrollRef}>
                <div className="site-container px-3 sm:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16">
                        <aside className="hidden lg:block lg:col-span-3 sticky top-32 h-fit">
                            <nav className="flex flex-col gap-1 p-2 bg-white rounded-3xl border border-[#E8F4FD] shadow-sm">
                                {sections.map((s, i) => (
                                    <a key={i} href={`#${s.id}`} className="flex items-center justify-between px-5 py-3.5 rounded-2xl hover:bg-[#EDF0FC] group transition-all duration-300">
                                        <span className="font-sora font-bold text-[14px] text-[#4a5568] group-hover:text-[#1B35AC]">{s.title}</span>
                                        <ChevronRight size={14} className="text-[#94a3b8] group-hover:translate-x-1 group-hover:text-[#1B35AC] transition-all" />
                                    </a>
                                ))}
                            </nav>
                            <div className="mt-8 p-6 rounded-3xl bg-white border border-[#E8F4FD] shadow-sm">
                                <div className="w-10 h-10 rounded-xl bg-[#E8F4FD] flex items-center justify-center mb-4">
                                    <HelpCircle size={18} className="text-[#2040CF]" />
                                </div>
                                <h4 className="font-sora font-bold text-[15px] text-[#0d1117] mb-2">Need Clarity?</h4>
                                <p className="text-[13px] text-[#596875] leading-relaxed mb-4">Our legal team is here to help you understand institutional rights and responsibilities.</p>
                                <div className="flex items-center gap-1.5 text-[#2040CF] text-[12px] font-bold">
                                    View FAQ <ChevronRight size={12} />
                                </div>
                            </div>
                        </aside>

                        <div className="lg:col-span-9 flex flex-col gap-4 sm:gap-10">
                            {sections.map((section, idx) => (
                                <motion.div id={section.id} key={idx} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}
                                    className="bg-white rounded-[32px] p-8 md:p-12 border border-[#E8F4FD] shadow-sm hover:shadow-md transition-all duration-500 group">
                                    <div className="flex flex-col md:flex-row gap-10">
                                        <div className="w-14 h-14 rounded-2xl bg-[#EDF0FC] flex items-center justify-center text-[#2040CF] flex-shrink-0 group-hover:scale-110 group-hover:bg-[#1B35AC] group-hover:text-white transition-all duration-500">
                                            <section.icon size={24} />
                                        </div>
                                        <div className="flex flex-col flex-1">
                                            <div className="inline-block w-fit px-3 py-1 rounded-full bg-[#E8F4FD] text-[#2040CF] font-black text-[10px] uppercase tracking-widest mb-4">{section.tag}</div>
                                            <h2 className="font-sora font-black text-2xl md:text-3xl text-[#0d1117] mb-4 tracking-tight">{section.title}</h2>
                                            <p className="text-[16px] leading-[1.8] text-[#596875] font-medium mb-8 max-w-[640px]">{section.content}</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {section.details.map((detail, dIdx) => (
                                                    <div key={dIdx} className="flex items-center gap-3 p-3.5 rounded-2xl bg-[#FAFCFF] border border-[#E8F4FD] group-hover:border-[#3D5EE1]/20 transition-all">
                                                        <ShieldCheck size={14} className="text-[#2040CF]" />
                                                        <span className="text-[13px] font-bold text-[#1e293b]">{detail}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}

                            <motion.div initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                                className="rounded-[40px] p-10 md:p-16 bg-[#031526] text-white relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#3D5EE1]/10 rounded-full blur-[100px]" />
                                <div className="relative z-10 flex flex-col items-center text-center">
                                    <h3 className="font-sora font-black text-3xl md:text-4xl mb-6">Building a <span className="text-[#3D5EE1]">Transparent Future.</span></h3>
                                    <p className="text-white/60 text-[16px] mb-10 max-w-[500px] leading-relaxed">By using Kaympus, you&apos;re joining a community of schools committed to excellence and administrative integrity.</p>
                                    <a href={`mailto:${contacts.legalEmail}`} className="px-8 py-4 bg-white text-[#0d1117] rounded-2xl font-bold text-[14px] hover:shadow-xl hover:-translate-y-1 transition-all">
                                        Contact Legal Team
                                    </a>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
        <CTASection />
        <Footer />
        </div>
    );
}

